import React, { useEffect } from 'react'
import { ArrayField, useWatch } from 'react-hook-form'
import ControlledMultiSelectGroupInput from 'transfix-form/components/ControlledMultiSelectGroupInput.tsx/ControlledMultiSelectGroupInput'
import ControlledSelect from 'transfix-form/components/ControlledSelect'
import { TasksIcon } from 'transfix-icons/build'
import { Grid, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { IIssueAutomationRuleNodeShipmentInformationOperatorTypeEnum } from 'types/graphqlTypes'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import {
  SHIPMENT_INFORMATION_FIELD_OPTIONS,
  SHIPMENT_INFORMATION_FIELD_OPTIONS_M4,
  SHIPMENT_INFORMATION_FIELD_OPTIONS_M5,
  SHIPMENT_INFORMATION_OPERATOR_OPTIONS,
} from '../constants'
import { useIssueRuleFormContext, getErrorMessage, TIssueRuleFormFormSchema } from './RuleForm'
import TriggerCard from './TriggerCard'

const useStyles = makeStyles(() => ({
  selectOperator: {
    '& legend': {
      color: brandColors.coolGray5,
    },
  },
}))

interface IShipmentInformationCardProps {
  onAddTrigger: () => void
  isAddTriggerDisabled: boolean
  onRemove: () => void
  showClearButton: boolean
  onCancel: () => void
  index: number
  node: Partial<ArrayField<TIssueRuleFormFormSchema['nodes'][number], 'key'>>
}

const ShipmentInformationCard = ({
  index,
  isAddTriggerDisabled,
  node,
  onAddTrigger,
  onCancel,
  onRemove,
  showClearButton,
}: IShipmentInformationCardProps) => {
  const classes = useStyles()
  const isMpShipmentIssueAutomationM4Enabled = useFeatureFlag('mpShipmentIssueAutomationM4')
  const isMpShipmentIssueAutomationM5Enabled = useFeatureFlag('mpShipmentIssueAutomationM5')
  const { control, errors, setValue } = useIssueRuleFormContext()
  const nodesWatch:
    | Partial<ArrayField<TIssueRuleFormFormSchema['nodes'][number], 'key'>>[]
    | undefined = useWatch({
    control,
    name: 'nodes',
  })
  const otherShipmentInformationNodes =
    nodesWatch?.filter((node, nodeIndex) => node.isShipmentInformation && nodeIndex !== index) || []
  const currentShipmentInformationNode = nodesWatch?.find(
    (node, nodeIndex) => node.isShipmentInformation && nodeIndex === index
  )
  const operatorSelectedByOtherNode =
    otherShipmentInformationNodes[0]?.shipmentInformationOperator || null
  const operatorSelectedByCurrentNode =
    currentShipmentInformationNode?.shipmentInformationOperator || null
  const operatorOptions = operatorSelectedByOtherNode
    ? SHIPMENT_INFORMATION_OPERATOR_OPTIONS.filter(
        option => option.value !== operatorSelectedByOtherNode
      )
    : SHIPMENT_INFORMATION_OPERATOR_OPTIONS
  const operatorOptionsLength = operatorOptions.length

  const fieldsSelectedByOtherNode =
    otherShipmentInformationNodes[0]?.shipmentInformationFields || []

  let allFieldOptions = SHIPMENT_INFORMATION_FIELD_OPTIONS

  if (isMpShipmentIssueAutomationM5Enabled) {
    allFieldOptions = SHIPMENT_INFORMATION_FIELD_OPTIONS_M5
  } else if (isMpShipmentIssueAutomationM4Enabled) {
    allFieldOptions = SHIPMENT_INFORMATION_FIELD_OPTIONS_M4
  }

  const fieldsOptions = fieldsSelectedByOtherNode.length
    ? allFieldOptions.filter(option => !fieldsSelectedByOtherNode.includes(option.value))
    : allFieldOptions

  useEffect(() => {
    if (operatorOptionsLength === 1) {
      // If there is only one option available, select it
      setValue(`nodes[${index}].shipmentInformationOperator`, operatorOptions[0].value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operatorOptionsLength])

  const fieldLabels =
    operatorSelectedByCurrentNode ===
    IIssueAutomationRuleNodeShipmentInformationOperatorTypeEnum.is_true
      ? 'When all of the following are true'
      : 'When all of the following are false'

  return (
    <TriggerCard
      icon={<TasksIcon className='inline' size='xlarge' color='skyBlue6' />}
      isAddTriggerDisabled={isAddTriggerDisabled}
      onAddTrigger={onAddTrigger}
      onCancel={onCancel}
      onRemove={onRemove}
      showClearButton={showClearButton}
      title='Shipment Information'>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={5}>
          <ControlledSelect
            control={control}
            name={`nodes[${index}].shipmentInformationOperator`}
            defaultValue={node.shipmentInformationOperator}
            SelectProps={{
              options: operatorOptions,
              error: !!getErrorMessage(errors, `nodes[${index}].shipmentInformationOperator`),
              validationMessage: getErrorMessage(
                errors,
                `nodes[${index}].shipmentInformationOperator`
              ),
              disabled: operatorOptions.length === 1,
            }}
          />
        </Grid>
        <Grid item xs={12} lg={12} className={classes.selectOperator}>
          <ControlledMultiSelectGroupInput
            control={control}
            name={`nodes[${index}].shipmentInformationFields`}
            options={fieldsOptions}
            defaultValue={node.shipmentInformationFields}
            InputMultiSelectProps={{
              placeholder: 'Select Fields',
              label: operatorSelectedByCurrentNode ? fieldLabels : undefined,
              clearable: true,
              invalid: !!getErrorMessage(errors, `nodes[${index}].shipmentInformationFields`),
              validationMessage: getErrorMessage(
                errors,
                `nodes[${index}].shipmentInformationFields`
              ),
            }}
          />
        </Grid>
      </Grid>
    </TriggerCard>
  )
}

export default ShipmentInformationCard
